import {HttpErrorResponse, HttpEvent, HttpHandlerFn, HttpInterceptorFn, HttpRequest} from '@angular/common/http';
import {inject} from '@angular/core';
import {catchError, from, Observable, switchMap, throwError} from 'rxjs';
import {GlobalsService} from '../../../src/app/services/globals.service';
import {environment} from '../../../src/environments/environment';
import {SdkBridge} from '../../../src/app/sdk-bridge';

/**
 * HTTP Auth Interceptor
 * @param request Subject request
 * @param nextHandler Next handler in the chain
 * @returns Event
 */
export const httpAuthInterceptor: HttpInterceptorFn = (
    request: HttpRequest<unknown>,
    nextHandler: HttpHandlerFn
): Observable<HttpEvent<unknown>> => {
    const globalsService: GlobalsService = inject(GlobalsService);

    // Proactively refresh the token before attaching it; no network call if still valid
    const executeRequest = (req: HttpRequest<unknown>): Observable<HttpEvent<unknown>> => {
        if (req.url.startsWith(environment.backendBaseUrl) && globalsService.keycloakService?.getToken() != null) {
            return from(globalsService.keycloakService.updateToken()).pipe(
                switchMap(() => {
                    const authRequest = req.clone({
                        headers: req.headers.set('Authorization', 'Bearer ' + globalsService.keycloakService.getToken())
                    });
                    return nextHandler(authRequest);
                })
            );
        }
        return nextHandler(req);
    };

    return executeRequest(request).pipe(
        catchError((error: HttpErrorResponse) => {
            if (error.status === 401) {
                if (globalsService.router.url != '/login') {
                    // Redirect to the login page
                    globalsService.messageService.clear('auth-message');
                    globalsService.messageService.add({
                        key: 'auth-message',
                        severity: 'error',
                        summary: 'Authentication Failed',
                        life: 5000,
                        detail: 'Sorry, an error occured during the authentication. \nPlease try again or contact your administrator.'
                    });

                    setTimeout(() => {
                        globalsService.keycloakService.redirectToLogout();
                    }, 5000);
                }
            } else if (error.status === 403) {
                // Display Unauthorized message
                globalsService.messageService.clear('auth-message');
                globalsService.messageService.add({
                    key: 'auth-message',
                    severity: 'error',
                    summary: 'Unauthorized',
                    detail: 'Sorry, you are not authorized to do this action. \nPlease contact your administrator.',
                    life: 5000
                });
            } else if (error.status === 412) {
                // Display Unauthorized message
                globalsService.messageService.clear('auth-message');
                globalsService.messageService.add({
                    key: 'auth-message',
                    severity: 'error',
                    summary: 'Unauthorized',
                    detail: error.error.message,
                    life: 5000
                });
                globalsService.router.navigate([SdkBridge.pageNotFoundUrl()]);
            }
            return throwError(() => error);
        })
    );
};
